import ConsoleLayout from '@/components/layout/ConsoleLayout';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormSchemaCreate, formSchemaCreate } from './type';
import FormHugginFace from './FormHugginFace';
import FormModel from './FormModel';
import FormSpec from './FormSpec';
import { useEffect, useState } from 'react';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  RocketIcon,
} from '@radix-ui/react-icons';

import DisplayPricing from './DisplayPricing';
import DisplaySummary from './DisplaySummary';
import { Link, useNavigate } from 'react-router-dom';
import PATH_PAGE from '@/router/path';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import { useCreateDedicateInstance } from '@/service/one-click-deploy/hook';
import {
  CheckHuggingFaceSupportResponse,
  CreateDedicateInstancCode,
  OptimizationTechnique,
  ProviderInstance,
} from '@/service/one-click-deploy/type';
import DisplayConfig from './DisplayConfig';
import DisplayOptimized from './DisplayOptimized';
import { toastSomethingError } from '@/components/ui/toast-content';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { IS_DEV } from '@/constants';
import DisplayQuotaLimit from './DisplayQuotaLimit';
import { useGetQuotaLimit } from '@/service/quota-limit/hook';
import { Skeleton } from '@/components/ui/skeleton';

const OneClickDeployCreate = () => {
  const { orgId, workspaceId } = useParamWorkspaceId();
  const navigate = useNavigate();
  const [step, setStep] = useState<'huggingFace' | 'instance' | 'quota' | ''>(
    ''
  );
  const createInstance = useCreateDedicateInstance();
  const quotaLimit = useGetQuotaLimit();
  useEffect(() => {
    if (quotaLimit?.data) {
      if (
        quotaLimit.data.on_click_deploy.using >=
        quotaLimit.data.on_click_deploy.max
      ) {
        setStep('quota');
      } else {
        setStep('huggingFace');
      }
    }
    if (quotaLimit.isError) {
      toastSomethingError();
    }
  }, [quotaLimit?.data]);

  const form = useForm<FormSchemaCreate>({
    resolver: zodResolver(formSchemaCreate),
    defaultValues: {
      huggingFace: {
        link: '',
        token: '',
      },
      instanceName: '',
      instance: {
        gpu: '',
        provider: ProviderInstance.AWS,
        region: '',
        optimizationTechnique: OptimizationTechnique.Basic,
      },
      model: {
        modelName: '',
      },
    },
  });

  const handleSubmitCheckModelSupportSuccess = () => {
    setStep('instance');
  };

  const onSubmit = async (data: z.infer<typeof formSchemaCreate>) => {
    try {
      const { instance_id, code } = await createInstance.mutateAsync({
        instance_name: data.instanceName,
        model_repo: data.huggingFace.link,
        model_token: data.huggingFace.token || undefined,
        optimize_technique: data.instance.optimizationTechnique,
        pricing_id: data.instance.gpu,
        workspace_id: workspaceId,
        is_dry_run: IS_DEV,
      });
      if (!instance_id) {
        throw new Error('Instance id is not found');
      }
      if (code === CreateDedicateInstancCode.SucceedRequestInstance) {
        toast.message('Start instance successfully');
        navigate(
          PATH_PAGE.dashboard.oneClickDeploy.byId.deployments(
            orgId,
            workspaceId,
            instance_id
          )
        );
      }
    } catch (error) {
      toastSomethingError();
    }
  };

  const backStep = () => {
    setStep('huggingFace');
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="container mt-8 max-w-6xl">
          <div className="flex items-center gap-2">
            {/* <Link to={PATH_PAGE.dashboard.oneClickDeploy.root(workspaceId)}>
                <ArrowLeftIcon />
              </Link> */}
            <p className="text-lg font-medium">Create Instance</p>
          </div>
          <div className="mt-4 grid gap-4">
            {step === '' && (
              <>
                <Skeleton className="h-[125px] w-full rounded-xl" />
                <Skeleton className="h-8 w-full rounded-xl" />
              </>
            )}
            {step === 'quota' && (
              <>
                <DisplayQuotaLimit />
              </>
            )}
            {step === 'huggingFace' && (
              <>
                <FormHugginFace
                  onNextStep={handleSubmitCheckModelSupportSuccess}
                />
              </>
            )}
            {step === 'instance' && (
              <>
                <Button
                  variant={'ghost'}
                  type="button"
                  onClick={backStep}
                  startIcon={<ArrowLeftIcon />}
                  className="justify-self-start"
                >
                  Back
                </Button>
                <FormModel />
                <FormSpec />
                {/* <DisplayConfig /> */}
                {/* <DisplayOptimized /> */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <DisplayPricing />
                  <DisplaySummary />
                </div>
                <Button
                  type="submit"
                  startIcon={<RocketIcon />}
                  variant={'primary'}
                  disabled={createInstance.isPending}
                >
                  Start Deploy
                </Button>
                {createInstance.data &&
                  createInstance.data.code !==
                    CreateDedicateInstancCode.SucceedRequestInstance && (
                    <Alert variant="destructive">
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      <AlertTitle className="first-letter:uppercase">
                        {createInstance?.data?.code}
                      </AlertTitle>
                      {createInstance.data.code ===
                        CreateDedicateInstancCode.InsufficientBalanceForRequestInstance && (
                        <AlertDescription>
                          please go to your{' '}
                          <Link
                            to={PATH_PAGE.setting.payment}
                            className="underline"
                          >
                            payment.
                          </Link>
                        </AlertDescription>
                      )}
                      {createInstance.data.code ===
                        CreateDedicateInstancCode.InsufficientQuotaForRequestInstance && (
                        <AlertDescription>
                          please go to your.{' '}
                          <Link
                            to={PATH_PAGE.setting.serviceQuota}
                            className="underline"
                          >
                            quota.
                          </Link>
                        </AlertDescription>
                      )}
                    </Alert>
                  )}
              </>
            )}
          </div>
        </div>
      </form>
    </Form>
  );
};

export default OneClickDeployCreate;
